import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Box, Stack, TextField, ToggleButton, ToggleButtonGroup, IconButton, Tooltip,
} from '@mui/material';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import InsertChartOutlinedIcon from '@mui/icons-material/InsertChartOutlined';
import { useSalonContextStore } from '../../store/salonContextStore';
import { attendanceApi, AttendanceSession } from '../../services/employeeService';
import { GroupedBarChart } from '../../components/charts/SimpleCharts';
import { PageHeader, SectionCard, StatTile, EmptyState, LoadingState } from '../../components/ui';
import { CHART_PALETTE, tokens } from '../../theme';
import {
  countLabel, formatDateLong, formatDayMonth, formatDuration, formatMonthYear, formatNumber,
} from '../../utils/format';

type ViewMode = 'day' | 'week' | 'month';

const pad = (n: number) => String(n).padStart(2, '0');
const toISO = (d: Date) => `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
const WEEKDAY_SHORT = ['Pon', 'Wt', 'Śr', 'Czw', 'Pt', 'Sob', 'Nd'];
const capitalize = (s: string) => s.charAt(0).toUpperCase() + s.slice(1);

/** Monday of the week containing the given date. */
const mondayOf = (d: Date) => {
  const monday = new Date(d);
  monday.setDate(d.getDate() - ((d.getDay() + 6) % 7));
  return monday;
};

const SHIFT_LABEL: Record<ViewMode, [string, string]> = {
  day: ['Poprzedni dzień', 'Następny dzień'],
  week: ['Poprzedni tydzień', 'Następny tydzień'],
  month: ['Poprzedni miesiąc', 'Następny miesiąc'],
};

export const TimeDashboardPage: React.FC = () => {
  const { activeSalonId } = useSalonContextStore();
  const now = new Date();
  const [view, setView] = useState<ViewMode>('month');
  const [date, setDate] = useState(toISO(now)); // kotwica dla widoku dnia i tygodnia
  const [month, setMonth] = useState(`${now.getFullYear()}-${pad(now.getMonth() + 1)}`);
  const [sessions, setSessions] = useState<AttendanceSession[]>([]);
  const [loading, setLoading] = useState(false);

  const range = useMemo(() => {
    if (view === 'month') {
      const [y, m] = month.split('-').map(Number);
      return { from: `${y}-${pad(m)}-01`, to: `${y}-${pad(m)}-${pad(new Date(y, m, 0).getDate())}` };
    }
    const anchor = new Date(`${date}T00:00:00`);
    if (view === 'day') return { from: date, to: date };
    const mon = mondayOf(anchor);
    const sun = new Date(mon);
    sun.setDate(mon.getDate() + 6);
    return { from: toISO(mon), to: toISO(sun) };
  }, [view, date, month]);

  const load = useCallback(async () => {
    if (!activeSalonId) return;
    setLoading(true);
    try {
      setSessions(await attendanceApi.listSessions({ salonId: activeSalonId, from: range.from, to: range.to }));
    } finally { setLoading(false); }
  }, [activeSalonId, range.from, range.to]);

  useEffect(() => { load(); }, [load]);

  const shift = (dir: -1 | 1) => {
    if (view === 'month') {
      const [y, m] = month.split('-').map(Number);
      const d = new Date(y, m - 1 + dir, 1);
      setMonth(`${d.getFullYear()}-${pad(d.getMonth() + 1)}`);
    } else {
      const d = new Date(`${date}T00:00:00`);
      d.setDate(d.getDate() + dir * (view === 'day' ? 1 : 7));
      setDate(toISO(d));
    }
  };

  const totalActual = sessions.reduce((s, x) => s + x.actualWorkMinutes, 0);
  const totalBillable = sessions.reduce((s, x) => s + x.billableWorkMinutes, 0);
  const totalBreaks = sessions.reduce((s, x) => s + x.actualBreakMinutes, 0);
  const breakCount = sessions.reduce((s, x) => s + (x.events?.filter((e) => e.type === 'BREAK_OUT').length || 0), 0);
  const unclosed = sessions.filter((x) => x.needsReview).length;
  const days = new Set(sessions.map((s) => s.workDate.slice(0, 10))).size;
  const diff = totalActual - totalBillable;

  // Wykres zależny od widoku: miesiąc → dni; tydzień → 7 dni Pon–Nd; dzień → pracownicy
  const chart = useMemo(() => {
    if (view === 'day') {
      const byStaff: Record<string, { a: number; b: number }> = {};
      sessions.forEach((s) => {
        const name = s.staff ? `${s.staff.firstName} ${s.staff.lastName}` : 'Nieznany';
        byStaff[name] = byStaff[name] || { a: 0, b: 0 };
        byStaff[name].a += s.actualWorkMinutes / 60;
        byStaff[name].b += s.billableWorkMinutes / 60;
      });
      const cats = Object.keys(byStaff).sort();
      return { cats, data: byStaff, unit: 'Godziny według pracowników' };
    }
    if (view === 'week') {
      const mon = mondayOf(new Date(`${date}T00:00:00`));
      const byDay: Record<string, { a: number; b: number }> = {};
      const cats: string[] = [];
      const keyToLabel: Record<string, string> = {};
      for (let i = 0; i < 7; i += 1) {
        const d = new Date(mon);
        d.setDate(mon.getDate() + i);
        const key = toISO(d);
        const label = `${WEEKDAY_SHORT[i]} ${pad(d.getDate())}.${pad(d.getMonth() + 1)}`;
        cats.push(label);
        keyToLabel[key] = label;
        byDay[label] = { a: 0, b: 0 };
      }
      sessions.forEach((s) => {
        const label = keyToLabel[s.workDate.slice(0, 10)];
        if (!label) return;
        byDay[label].a += s.actualWorkMinutes / 60;
        byDay[label].b += s.billableWorkMinutes / 60;
      });
      return { cats, data: byDay, unit: 'Godziny w kolejnych dniach tygodnia' };
    }
    const byDay: Record<string, { a: number; b: number }> = {};
    sessions.forEach((s) => {
      const d = s.workDate.slice(8, 10);
      byDay[d] = byDay[d] || { a: 0, b: 0 };
      byDay[d].a += s.actualWorkMinutes / 60;
      byDay[d].b += s.billableWorkMinutes / 60;
    });
    return { cats: Object.keys(byDay).sort(), data: byDay, unit: 'Godziny w dniach, w których ktoś pracował' };
  }, [view, sessions, date]);

  const periodLabel = capitalize(view === 'day'
    ? formatDateLong(`${date}T00:00:00`)
    : view === 'week'
      ? `${formatDayMonth(`${range.from}T00:00:00`)} – ${formatDayMonth(`${range.to}T00:00:00`)} ${range.to.slice(0, 4)}`
      : formatMonthYear(`${range.from}T00:00:00`));

  return (
    <Box>
      <PageHeader
        breadcrumbs={[{ label: 'Zespół' }]}
        title="Czas pracy"
        description={loading
          ? periodLabel
          : `${periodLabel} · ${countLabel(sessions.length, 'zmiana', 'zmiany', 'zmian')}`}
      >
        <Stack direction="row" alignItems="center" gap={1} flexWrap="wrap">
          <ToggleButtonGroup size="small" exclusive value={view} onChange={(_, v) => v && setView(v)} aria-label="Zakres">
            <ToggleButton value="day">Dzień</ToggleButton>
            <ToggleButton value="week">Tydzień</ToggleButton>
            <ToggleButton value="month">Miesiąc</ToggleButton>
          </ToggleButtonGroup>
          <Stack direction="row" alignItems="center" gap={0.5}>
            <Tooltip title={SHIFT_LABEL[view][0]}>
              <IconButton onClick={() => shift(-1)} aria-label={SHIFT_LABEL[view][0]}><ChevronLeftIcon /></IconButton>
            </Tooltip>
            {view === 'month' ? (
              <TextField type="month" size="small" value={month} onChange={(e) => setMonth(e.target.value)} inputProps={{ 'aria-label': 'Miesiąc' }} sx={{ width: 190 }} />
            ) : (
              <TextField type="date" size="small" value={date} onChange={(e) => e.target.value && setDate(e.target.value)} inputProps={{ 'aria-label': view === 'day' ? 'Dzień' : 'Dzień w tygodniu' }} sx={{ width: 190 }} />
            )}
            <Tooltip title={SHIFT_LABEL[view][1]}>
              <IconButton onClick={() => shift(1)} aria-label={SHIFT_LABEL[view][1]}><ChevronRightIcon /></IconButton>
            </Tooltip>
          </Stack>
        </Stack>
      </PageHeader>

      {loading ? (
        <Stack gap={2.5}>
          <LoadingState variant="tiles" count={4} />
          <LoadingState variant="tiles" count={4} />
          <LoadingState variant="block" height={220} />
        </Stack>
      ) : (
        <Stack gap={2.5}>
          <Box sx={{ display: 'grid', gap: 2, gridTemplateColumns: { xs: 'repeat(2, minmax(0, 1fr))', md: 'repeat(4, minmax(0, 1fr))' } }}>
            <StatTile label="Czas rozliczeniowy" value={formatDuration(totalBillable)} hint="Podstawa do wynagrodzeń" />
            <StatTile label="Czas faktyczny" value={formatDuration(totalActual)} hint="Od wejścia do wyjścia" />
            <StatTile
              label="Różnica"
              value={`${diff < 0 ? '−' : ''}${formatDuration(Math.abs(diff))}`}
              valueColor={diff > 0 ? tokens.warningText : undefined}
              hint="Faktyczny minus rozliczeniowy"
            />
            <StatTile label="Przepracowane dni" value={formatNumber(days)} hint={countLabel(sessions.length, 'zmiana', 'zmiany', 'zmian')} />
            <StatTile label="Liczba wyjść" value={formatNumber(breakCount)} hint="Przerwy w trakcie zmian" />
            <StatTile label="Suma wyjść" value={formatDuration(totalBreaks)} hint="Łączny czas przerw" />
            <StatTile
              label="Niezamknięte zmiany"
              value={formatNumber(unclosed)}
              valueColor={unclosed ? tokens.danger : undefined}
              hint={unclosed ? 'Wymagają korekty w kalendarzu pracy' : 'Wszystkie zmiany zamknięte'}
            />
            <StatTile label="Liczba zmian" value={formatNumber(sessions.length)} hint="Rozpoczęte w tym okresie" />
          </Box>

          <SectionCard title="Czas faktyczny i rozliczeniowy" subtitle={chart.unit}>
            {sessions.length === 0 ? (
              <EmptyState
                compact
                icon={<InsertChartOutlinedIcon />}
                title="Brak zmian w tym okresie"
                description="Wybierz inny okres albo dodaj brakujący wpis w kalendarzu pracy."
              />
            ) : (
              <GroupedBarChart
                height={240}
                categories={chart.cats}
                series={[
                  { label: 'Faktyczny', color: CHART_PALETTE[0], values: chart.cats.map((c) => Math.round((chart.data[c]?.a || 0) * 10) / 10) },
                  { label: 'Rozliczeniowy', color: CHART_PALETTE[1], values: chart.cats.map((c) => Math.round((chart.data[c]?.b || 0) * 10) / 10) },
                ]}
                formatValue={(v) => `${formatNumber(v)} h`}
              />
            )}
          </SectionCard>
        </Stack>
      )}
    </Box>
  );
};

export default TimeDashboardPage;
